import React, { useState, useEffect } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import searchIcon from '../../assets/icons/search.svg';

interface Instructor {
  id: string;
  full_name: string;
  username: string;
  profile_picture_url?: string;
  belt_rank?: string;
}

interface InstructorAutocompleteProps {
  value: Instructor | string | null;
  onChange: (instructor: Instructor | string | null) => void;
  placeholder?: string;
  className?: string;
  containerClassName?: string;
  disabled?: boolean;
}

export function InstructorAutocomplete({ 
  value, 
  onChange, 
  placeholder = "Type a name or a username", 
  className = "", 
  containerClassName = "",
  disabled = false 
}: InstructorAutocompleteProps) {
  const [filteredInstructors, setFilteredInstructors] = useState<Instructor[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [showManualOption, setShowManualOption] = useState(false);

  // Extract display value from the value prop
  const displayValue = typeof value === 'string' ? value : (value && typeof value === 'object' ? value.full_name || '' : '');

  useEffect(() => {
    setInputValue(displayValue || '');
  }, [displayValue]);

  // Scroll to dropdown when it opens
  useEffect(() => {
    if (open && (filteredInstructors.length > 0 || showManualOption)) {
      const dropdown = document.querySelector('[data-instructor-dropdown]');
      if (dropdown) {
        dropdown.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'nearest'
        });
      }
    }
  }, [open, filteredInstructors.length, showManualOption]);

  useEffect(() => {
    const fetchInstructors = async () => {
      if (!inputValue || inputValue.length < 2) {
        setFilteredInstructors([]);
        setShowManualOption(false);
        return;
      }
      
      setLoading(true);
      try {
        const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/search_instructors`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            searchTerm: inputValue,
            limit: 10
          })
        });

        if (response.ok) {
          const result = await response.json();
          let instructors = result.data || [];
          // Remove duplicates by id
          const seen = new Set();
          instructors = instructors.filter((inst: Instructor) => {
            if (seen.has(inst.id)) return false;
            seen.add(inst.id);
            return true;
          });
          setFilteredInstructors(instructors);
          
          // Show manual option if no exact matches found
          const hasExactMatch = instructors.some(
            (instructor: Instructor) => 
              (instructor.full_name?.toLowerCase() === inputValue.toLowerCase()) ||
              (instructor.username?.toLowerCase() === inputValue.toLowerCase())
          );
          setShowManualOption(!hasExactMatch && inputValue.length > 0);
        } else {
          console.error('Error fetching instructors:', response.statusText);
          setFilteredInstructors([]);
          setShowManualOption(false);
        }
      } catch (error) {
        console.error('Error fetching instructors:', error);
        setFilteredInstructors([]);
        setShowManualOption(false);
      } finally {
        setLoading(false);
      }
    };

    const timeoutId = setTimeout(fetchInstructors, 300);
    return () => clearTimeout(timeoutId);
  }, [inputValue]);

  const handleSelect = (instructor: Instructor | string) => {
    onChange(instructor);
    setInputValue(typeof instructor === 'string' ? instructor : instructor.full_name || '');
    setOpen(false);
    setShowManualOption(false);
  };

  const handleManualAdd = () => {
    handleSelect(inputValue);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setOpen(true);
    
    // Clear selection if input is cleared
    if (!newValue) {
      onChange(null);
    }
  };

  const handleInputFocus = () => {
    if (inputValue.length >= 2) {
      setOpen(true);
    }
  };

  const handleInputBlur = () => {
    // Delay closing to allow for clicks on dropdown items
    setTimeout(() => setOpen(false), 200);
  };

  return (
    <div className={cn("relative", containerClassName)}>
      <div className="relative">
        <img
          src={searchIcon}
          alt="Search"
          className={cn(
            "absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5",
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "brightness-0 opacity-40"
              : "brightness-0 invert opacity-60"
          )}
        />
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            "w-full border h-12 rounded-md pl-12 pr-12 text-[12px] focus:outline-none transition-all duration-200 font-sans",
            // Check if we're in a white background context (like a modal)
            containerClassName?.includes('bg-white') || className?.includes('bg-white')
              ? "bg-gray-50 border-gray-300 text-gray-900 placeholder-gray-500"
              : "border-white text-white placeholder-white/60 bg-white/5",
            className
          )}
          style={{ 
            zIndex: 1,
            borderWidth: '1px',
            borderStyle: 'solid'
          }}
        />
        {inputValue && (
          <button
            onClick={() => {
              setInputValue('');
              onChange(null);
            }}
            className={cn(
              "absolute right-4 top-1/2 transform -translate-y-1/2 transition-colors duration-200",
              containerClassName?.includes('bg-white') || className?.includes('bg-white')
                ? "text-gray-400 hover:text-gray-600"
                : "text-white/60 hover:text-white"
            )}
            aria-label="Clear search"
            style={{ zIndex: 2 }}
          >
            <X className="w-5 h-5" />
          </button>
        )}
        {loading && !inputValue && (
          <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none" style={{ zIndex: 2 }}>
            <div className={cn(
              "w-4 h-4 border-2 rounded-full animate-spin",
              containerClassName?.includes('bg-white') || className?.includes('bg-white')
                ? "border-gray-300 border-t-gray-600"
                : "border-white/30 border-t-white"
            )} />
          </div>
        )}
      </div>

      {open && (filteredInstructors.length > 0 || showManualOption) && (
        <div className="absolute z-50 w-full mt-1 bg-black rounded-md shadow-lg max-h-60 overflow-y-auto" data-instructor-dropdown>
          {loading ? (
            <div className="p-4 text-center text-sm text-white/60">
              Loading instructors...
            </div>
          ) : (
            <div className="py-1">
              {filteredInstructors.map((instructor) => (
                <div
                  key={instructor.id}
                  onClick={() => handleSelect(instructor)}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    {inputValue === instructor.full_name && <Check className="h-4 w-4 text-white" />}
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">{instructor.full_name || 'Unknown'}</span>
                    <span className="text-xs text-white/60">@{instructor.username || 'unknown'}</span>
                  </div>
                </div>
              ))}
              
              {showManualOption && (
                <div
                  onClick={handleManualAdd}
                  className="relative flex w-full cursor-pointer select-none items-center rounded-sm py-1.5 pl-8 pr-2 text-lg text-white hover:bg-white/10 focus:bg-white/10 border-t border-white/20"
                >
                  <span className="absolute left-2 flex h-3.5 w-3.5 items-center justify-center">
                    <Plus className="h-4 w-4 text-white" />
                  </span>
                  <div className="flex flex-col flex-1">
                    <span className="text-sm font-medium text-white">
                      Add "{inputValue}" manually
                    </span>
                    <span className="text-xs text-white/60">Manual instructor</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}; 